import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight,
  CheckCircle,
  Users,
  Target,
  Award,
  Handshake,
  MapPin,
  Clock,
  Shield,
  Lightbulb
} from "lucide-react";

const AboutPage = () => {
  const values = [
    {
      icon: Handshake,
      title: "Partnerschaft",
      description: "Wir sehen uns als Partner unserer Kunden und arbeiten gemeinsam an optimalen Lösungen."
    },
    {
      icon: Shield,
      title: "Zuverlässigkeit",
      description: "Pünktlichkeit und Verlässlichkeit sind die Grundpfeiler unserer täglichen Arbeit."
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "Wir setzen auf moderne Technologien und entwickeln uns stetig weiter."
    },
    {
      icon: Users,
      title: "Persönlichkeit",
      description: "Bei uns haben Sie immer einen direkten Ansprechpartner, der Ihre Bedürfnisse kennt."
    }
  ];

  const timeline = [
    {
      year: "Gründung",
      title: "Startschuss in Sulzbach",
      description: "Kauz Logistik wurde in Sulzbach gegründet mit der Vision, Logistik persönlicher und effizienter zu gestalten."
    },
    {
      year: "Wachstum",
      title: "Erweiterung des Leistungsportfolios",
      description: "Aufbau unserer Fulfillment-Kapazitäten und Erweiterung unseres Fuhrparks für Next-Day-Lieferungen."
    },
    {
      year: "Heute",
      title: "Ihr Full-Service Partner",
      description: "Ein etablierter Logistikdienstleister mit umfassendem Service für Unternehmen aller Größen."
    }
  ];

  const advantages = [
    "Persönliche Betreuung durch feste Ansprechpartner",
    "Flexible und skalierbare Logistiklösungen",
    "Moderne IT-Systeme mit Echtzeitdaten",
    "Strategisch günstige Lage im Rhein-Main-Gebiet",
    "Langjährige Erfahrung in der Logistikbranche",
    "Transparente Preisgestaltung ohne versteckte Kosten"
  ];

  return (
    <div data-testid="about-page">
      {/* Page Header */}
      <section className="page-header py-20 md:py-28 text-white">
        <div className="container mx-auto px-4 relative z-10">
          <span className="inline-block bg-orange-600 text-white text-sm uppercase tracking-widest font-bold px-4 py-2 mb-6">
            Über uns
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Kauz Logistik e.K.
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl">
            Ihr zuverlässiger Logistikpartner aus Sulzbach für die gesamte Rhein-Main-Region.
          </p>
        </div>
      </section>

      {/* About Intro */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
                Wer wir sind
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-6 text-slate-900">
                Logistik mit Leidenschaft
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Kauz Logistik e.K. ist Ihr kompetenter Ansprechpartner für Fulfillment, 
                Transportmanagement und Verpackungsservices in Sulzbach und der gesamten 
                Rhein-Main-Region.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Unter der Leitung von Geschäftsführer Waldemar Kauz vereinen wir langjährige 
                Branchenerfahrung mit modernster Technik. Unser Ziel: Ihre Logistik so 
                effizient und unkompliziert wie möglich zu gestalten.
              </p>
              
              <div className="bg-slate-50 p-6 border-l-4 border-orange-600">
                <p className="text-slate-700 italic">
                  "Unsere Kunden stehen im Mittelpunkt. Wir entwickeln Lösungen, die genau 
                  zu Ihren Anforderungen passen – persönlich, flexibel und zuverlässig."
                </p>
                <p className="text-slate-900 font-bold mt-4">– Waldemar Kauz, Geschäftsführer</p>
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/z0lbohlr_Transporter.jpg"
                alt="Transporter auf der Straße"
                className="w-full shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-orange-600 text-white p-6 hidden md:block">
                <MapPin size={32} className="mb-2" aria-hidden="true" />
                <div className="text-sm uppercase tracking-wider">Standort</div>
                <div className="font-bold">Sulzbach</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-slate-50" data-testid="values-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
              Unsere Werte
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 text-slate-900">
              Was uns antreibt
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div 
                  key={value.title}
                  className="bg-white p-8 border border-slate-200 card-hover"
                  data-testid={`value-card-${index}`}
                >
                  <div className="icon-box mb-6">
                    <Icon size={28} aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-slate-900">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-orange-600 uppercase tracking-widest font-bold text-sm">
              Unsere Geschichte
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 text-slate-900">
              Der Weg von Kauz Logistik
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto">
            {timeline.map((item, index) => (
              <div 
                key={item.year}
                className="flex gap-8 mb-12 last:mb-0"
                data-testid={`timeline-item-${index}`}
              >
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-slate-950 text-white flex items-center justify-center font-bold text-sm">
                    {item.year}
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 h-full bg-slate-200 mt-4"></div>
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <h3 className="font-bold text-xl text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="dark-section text-white py-20" data-testid="advantages-section">
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-orange-500 uppercase tracking-widest font-bold text-sm">
                Ihre Vorteile
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-8">
                Warum Kauz Logistik?
              </h2>
              
              <div className="space-y-4">
                {advantages.map((advantage, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <CheckCircle className="text-orange-500 flex-shrink-0 mt-0.5" size={24} aria-hidden="true" />
                    <span className="text-slate-300 text-lg">{advantage}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              {[
                { icon: Target, label: "Zielorientiert", value: "100%" },
                { icon: Clock, label: "Pünktlichkeit", value: "99%" },
                { icon: Users, label: "Kundenzufriedenheit", value: "Hoch" },
                { icon: Award, label: "Qualitätsstandard", value: "Premium" }
              ].map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={stat.label} className="bg-slate-800/50 p-6 text-center" data-testid={`advantage-stat-${index}`}>
                    <Icon className="mx-auto mb-3 text-orange-500" size={32} aria-hidden="true" />
                    <div className="mono text-2xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-slate-400 text-sm uppercase tracking-wider">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="bg-slate-50 p-8 md:p-12 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="font-bold text-2xl mb-6 text-slate-900">Firmendaten</h3>
                <div className="space-y-4 text-slate-600">
                  <p><strong className="text-slate-900">Firma:</strong><br />Kauz Logistik e.K.</p>
                  <p><strong className="text-slate-900">Geschäftsführer:</strong><br />Waldemar Kauz</p>
                  <p><strong className="text-slate-900">Adresse:</strong><br />Industriestr. 18<br />63834 Sulzbach</p>
                </div>
              </div>
              <div>
                <h3 className="font-bold text-2xl mb-6 text-slate-900">Kontakt</h3>
                <div className="space-y-4 text-slate-600">
                  <p><strong className="text-slate-900">Telefon:</strong><br />06028 4062310</p>
                  <p><strong className="text-slate-900">E-Mail:</strong><br />hallo@kauzlogistik.de</p>
                  <p><strong className="text-slate-900">Web:</strong><br />www.kauzlogistik.de</p>
                </div>
              </div>
            </div>
            
            <div className="mt-8 pt-8 border-t border-slate-200">
              <Link to="/kontakt">
                <Button 
                  className="bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-wider font-bold px-8"
                  data-testid="about-contact-button"
                >
                  Kontakt aufnehmen
                  <ArrowRight className="ml-2" size={16} aria-hidden="true" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
